import closeIcon from '@jetbrains/icons/close.svg'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {Size} from '@jetbrains/ring-ui/components/toggle/toggle'
import classNames from 'classnames'
import * as React from 'react'
import type {MouseEventHandler} from 'react'

import {useDistanceToNow} from '../../../../../hooks/useTimeSelector'
import {getAgentHref} from '../../../../../routes'
import {getCanViewAgentDetails} from '../../../../../selectors'
import type {NormalizedAgent} from '../../../../../types'
import {stringifyId} from '../../../../../types'
import AgentLink from '../../../../common/AgentLink/AgentLink'
import Icon from '../../../../common/Icon/Icon'
import IconButton from '../../../../common/IconButton/IconButton'
import {STRING_DEFAULT_AGENT_POOL_ID} from '../../AgentPoolPage/AgentPoolPage.constants'
import {EnableAgentForm} from '../EnableAgentForm/EnableAgentForm'

import {useMoveAgentToAgentPool} from './AgentListView.hooks'
import DisconnectionComment from './DisconnectionComment/DisconnectionComment'
import StatusComment from './StatusComment/StatusComment'

import styles from './AgentListView.css'

import {useAppSelector} from 'src/hooks/react-redux'

function AgentListView({
  agent,
  className,
  removable,
}: {
  agent: NormalizedAgent
  className?: string
  removable?: boolean
}) {
  const {
    id,
    connected,
    name,
    pool,
    enabled = false,
    osType,
    disconnectionComment,
    lastActivityTime,
    typeId,
  } = agent
  const {comment: enableComment, statusSwitchTime} = agent.enabledInfo || {}
  const statusSwitchFromNow = useDistanceToNow(statusSwitchTime)
  const lastActivityFromNow = useDistanceToNow(lastActivityTime)
  const [moveAgentToDeaultPool, movingAgentToDeaultPool] = useMoveAgentToAgentPool(
    stringifyId(agent.id),
    STRING_DEFAULT_AGENT_POOL_ID,
  )

  return (
    <div data-test="agent" className={classNames(className, styles.agent)}>
      {connected ? (
        <EnableAgentForm
          poolId={pool.id}
          typeId={typeId}
          className={styles.toggle}
          id={id}
          name={name}
          enabled={enabled}
          authorized
          size={Size.Size20}
          pale={false}
        />
      ) : null}
      <AgentLink
        isSakuraUI
        name={name}
        className={styles.link}
        {...(useAppSelector(getCanViewAgentDetails)
          ? {
              href: getAgentHref(id),
              agentId: id,
              osType,
            }
          : {})}
      />
      {connected && !('build' in agent) && <div className={styles.idle}>{'Idle'}</div>}
      {'build' in agent && agent.build == null && (
        <div className={styles.runningPlaceholder}>
          <Icon className={styles.icon} name="running_green" />
          {'Running a build. You do not have permissions to see the build details.'}
        </div>
      )}
      {connected ? null : (
        <DisconnectionComment className={styles.disconnectionComment}>
          {disconnectionComment}
        </DisconnectionComment>
      )}
      {enableComment?.text != null ? (
        <StatusComment
          statusTitle={enabled ? 'Enabled' : 'Disabled'}
          text={enableComment.text}
          user={enableComment.user}
          timestamp={enableComment.timestamp}
          className={styles.statusComment}
        />
      ) : null}
      <div className={styles.split} />
      {connected && statusSwitchFromNow != null ? (
        <div className={styles.enableTimer}>
          {`Will be ${enabled ? 'off' : 'on'} ${statusSwitchFromNow}`}
        </div>
      ) : null}
      {!connected && lastActivityFromNow != null ? (
        <div
          className={styles.lastCommunicationTimer}
        >{`Last communication ${lastActivityFromNow}`}</div>
      ) : null}
      {removable ? (
        movingAgentToDeaultPool ? (
          <LoaderInline className={styles.loader} />
        ) : (
          <IconButton
            onClick={moveAgentToDeaultPool as MouseEventHandler<HTMLButtonElement>}
            icon={closeIcon}
            className={styles.unassignButton}
            title="Move to the Default agent pool"
          />
        )
      ) : null}
    </div>
  )
}

export default AgentListView
